import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Info, Clock } from 'lucide-react';

const QuestionnaireRecap = ({ 
  eventType,
  eventDetails = {},
  nbInvites,
  wantsMagnets = false,
  hasHenne = false,
  selectedPrestations, 
  onSelectionChange,
  showPrices = true 
}) => {
  const [prestations, setPrestations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPrestations();
  }, [eventType, nbInvites, hasHenne, eventDetails.date_mairie, eventDetails.date_henne, eventDetails.date_synagogue, eventDetails.date_reception]);

  const loadPrestations = async () => {
    if (!eventType || eventType === 'autre') {
      setPrestations([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Construire les dates pour l'API
      const datesArray = [];
      if (eventDetails.date_mairie) {
        datesArray.push({ date: eventDetails.date_mairie, type: 'mairie', lieu: 'mairie' });
      }
      if (eventDetails.date_henne) {
        datesArray.push({ date: eventDetails.date_henne, type: 'henne', lieu: 'henne' });
      }
      if (eventDetails.date_synagogue) {
        datesArray.push({ date: eventDetails.date_synagogue, type: 'synagogue', lieu: 'synagogue' });
      }
      if (eventDetails.date_reception) {
        datesArray.push({ date: eventDetails.date_reception, type: 'reception', lieu: 'reception' });
      }

      // Appeler l'API qui retourne les prestations filtrées avec is_checked
      const response = await axios.post(`${API}/prestations/for-questionnaire`, {
        event_type: eventType,
        nb_invites: nbInvites || '',
        has_henne: hasHenne,
        has_vin_honneur: false, // À récupérer depuis les données si disponible
        dates: datesArray
      });

      const prestationsWithChecked = response.data || [];
      setPrestations(prestationsWithChecked);

      // Pré-cocher les prestations qui ont is_checked = true
      const prestationsToCheck = prestationsWithChecked
        .filter(p => p.is_checked)
        .map(p => p.id);
      
      if (prestationsToCheck.length > 0) {
        // Ne pré-cocher que celles qui ne sont pas déjà sélectionnées
        const newIds = prestationsToCheck.filter(id => !selectedPrestations.includes(id));
        if (newIds.length > 0) {
          const uniqueIds = [...new Set([...selectedPrestations, ...newIds])];
          onSelectionChange(uniqueIds);
        }
      }
    } catch (error) {
      console.error('Error loading prestations:', error);
      setPrestations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (id) => {
    if (selectedPrestations.includes(id)) {
      onSelectionChange(selectedPrestations.filter(pid => pid !== id));
    } else {
      onSelectionChange([...selectedPrestations, id]);
    }
  };

  const getUSBInfo = () => {
    return eventType === 'mariage' ? '3 Clés USB avec boîtier personnalisé' : '1 Clé USB avec boîtier personnalisé';
  };

  const calculateTotal = () => {
    const selected = prestations.filter(p => selectedPrestations.includes(p.id));
    const totalHT = selected.reduce((sum, p) => sum + (p.price_ht || 0), 0);
    return { totalHT, totalTTC: totalHT * 1.2 };
  };

  const renderPrestationWithOptions = (prestation) => {
    const isSelected = selectedPrestations.includes(prestation.id);
    
    // Trouver les options liées à cette prestation (même type photo/vidéo + même lieu)
    const relatedOptions = prestations.filter(opt => {
      if (opt.category !== 'option') return false;
      const optName = opt.name.toLowerCase();
      const prestName = prestation.name.toLowerCase();
      
      // Photographe -> seulement options photographe
      const isPrestPhoto = prestName.includes('photo');
      const isOptPhoto = optName.includes('photo');
      
      // Vidéaste -> seulement options vidéo/caméra
      const isPrestVideo = prestName.includes('vidéo') || prestName.includes('vidéaste');
      const isOptVideo = optName.includes('caméra') || optName.includes('vidéo');
      
      // Vérifier correspondance type
      if (isPrestPhoto && !isOptPhoto) return false;
      if (isPrestVideo && !isOptVideo) return false;
      
      // Mairie
      if (prestName.includes('mairie')) {
        return optName.includes('mairie');
      }
      // Henné
      if (prestName.includes('henné') || prestName.includes('henne')) {
        return optName.includes('henné') || optName.includes('henne');
      }
      // Synagogue
      if (prestName.includes('synagogue')) {
        return optName.includes('synagogue');
      }
      // Réception (Forfait Mariage)
      if (prestName.includes('mariage') && !prestName.includes('mairie')) {
        // Pour le forfait photo mariage, afficher les options photographe réception
        if (isPrestPhoto && isOptPhoto) {
          return optName.includes('réception') || optName.includes('photographe');
        }
        // Pour le forfait vidéo mariage, afficher les options vidéaste réception
        if (isPrestVideo && isOptVideo) {
          return optName.includes('réception') || optName.includes('caméra') || optName.includes('vidéo');
        }
      }
      
      return false;
    });

    return (
      <div key={prestation.id} className="space-y-2">
        <div
          className={`flex items-start gap-3 p-3 border rounded-lg transition-all cursor-pointer ${
            isSelected ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:bg-stone-50'
          }`}
          onClick={() => handleToggle(prestation.id)}
        >
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => handleToggle(prestation.id)}
            className="mt-1"
          />
          <div className="flex-1" onClick={(e) => e.stopPropagation()}>
            <label 
              className="font-medium text-stone-900 cursor-pointer flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                handleToggle(prestation.id);
              }}
            >
              {prestation.name}
              {prestation.is_base_package && <Badge className="bg-green-100 text-green-800 text-xs">📦 Inclus</Badge>}
              {prestation.is_recommended && <Badge className="bg-amber-100 text-amber-800 text-xs">⭐ Suggéré</Badge>}
            </label>
            {prestation.description && <p className="text-sm text-stone-600 mt-1">{prestation.description}</p>}
          </div>
          {showPrices && (
            <div className="text-right">
              <p className="font-bold text-stone-900">{prestation.price_ht.toFixed(2)}€</p>
              <p className="text-xs text-stone-500">HT</p>
            </div>
          )}
        </div>
        
        {/* Options liées affichées SOUS la prestation */}
        {relatedOptions.length > 0 && (
          <div className="ml-8 space-y-2">
            {relatedOptions.map(opt => {
              const optSelected = selectedPrestations.includes(opt.id);
              return (
                <div
                  key={opt.id}
                  className={`flex items-start gap-3 p-2 border rounded-lg transition-all cursor-pointer ${
                    optSelected ? 'border-blue-500 bg-blue-50' : 'border-stone-200 hover:bg-stone-50'
                  }`}
                  onClick={() => handleToggle(opt.id)}
                >
                  <Checkbox
                    checked={optSelected}
                    onCheckedChange={() => handleToggle(opt.id)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label 
                      className="text-sm font-medium text-stone-800 cursor-pointer flex items-center gap-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggle(opt.id);
                      }}
                    >
                      {opt.name}
                      <Badge className="bg-blue-100 text-blue-800 text-xs">+ Option</Badge>
                    </label>
                  </div>
                  {showPrices && (
                    <div className="text-right">
                      <p className="font-semibold text-stone-900 text-sm">{opt.price_ht.toFixed(2)}€</p>
                      <p className="text-xs text-stone-500">HT</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <Card><CardContent className="p-6"><p className="text-center text-stone-500">Chargement...</p></CardContent></Card>;
  }

  const { totalHT, totalTTC } = calculateTotal();
  
  // Magnets : UNIQUEMENT celui adapté (100 supplémentaires est dans Options)
  const getRelevantMagnets = () => {
    if (!wantsMagnets) return [];
    
    const selectedMagnets = prestations.filter(p => 
      (p.category === 'magnets' || p.prestation_type === 'magnets') &&
      selectedPrestations.includes(p.id) &&
      !p.name.toLowerCase().includes('supplémentaire')
    );
    
    return selectedMagnets;
  };
  
  const generalOptions = prestations.filter(p => 
    p.category === 'option' && 
    !p.name.toLowerCase().includes('mairie') &&
    !p.name.toLowerCase().includes('henné') &&
    !p.name.toLowerCase().includes('henne') &&
    !p.name.toLowerCase().includes('synagogue') &&
    !p.name.toLowerCase().includes('réception')
  );

  // === MARIAGE : AFFICHAGE CHRONOLOGIQUE PAR ÉVÉNEMENT ===
  if (eventType === 'mariage') {
    // Filtrer les prestations principales (pas les options ni les magnets)
    const mainPrestations = prestations.filter(p => 
      p.category !== 'option' && 
      p.category !== 'magnets' &&
      p.prestation_type !== 'magnets'
    );
    
    // Organiser les prestations par événement en fonction de leur nom
    const prestationsMairie = mainPrestations.filter(p => {
      const name = p.name.toLowerCase();
      return name.includes('mairie') && !name.includes('réception');
    });
    
    const prestationsHenne = mainPrestations.filter(p => {
      const name = p.name.toLowerCase();
      return name.includes('henné') || name.includes('henne');
    });
    
    const prestationsReception = mainPrestations.filter(p => {
      const name = p.name.toLowerCase();
      return (name.includes('réception') || name.includes('mariage')) && 
             !name.includes('mairie') && 
             !name.includes('henné') && 
             !name.includes('henne');
    });
    
    // Construire les événements avec toutes les prestations disponibles
    const events = [];
    
    // Mairie - afficher si des prestations existent
    if (prestationsMairie.length > 0) {
      const mairieDate = eventDetails.date_mairie ? new Date(eventDetails.date_mairie) : null;
      events.push({
        date: mairieDate || new Date('2099-12-31'), // Date future si non renseignée pour tri
        label: 'Mairie',
        hour: eventDetails.hour_mairie || '',
        icon: '🏛️',
        prestations: prestationsMairie
      });
    }
    
    // Henné - afficher si des prestations existent et si henné est sélectionné
    if (prestationsHenne.length > 0 && (hasHenne || prestationsHenne.length > 0)) {
      const henneDate = eventDetails.date_henne ? new Date(eventDetails.date_henne) : null;
      events.push({
        date: henneDate || new Date('2099-12-31'), // Date future si non renseignée pour tri
        label: 'Henné',
        hour: eventDetails.hour_henne || '',
        icon: '💅',
        prestations: prestationsHenne
      });
    }
    
    // Réception - afficher si des prestations existent
    if (prestationsReception.length > 0) {
      const receptionDate = eventDetails.date_reception ? new Date(eventDetails.date_reception) : null;
      events.push({
        date: receptionDate || new Date('2099-12-31'), // Date future si non renseignée pour tri
        label: 'Réception',
        hour: eventDetails.hour_reception || '',
        icon: '🎉',
        prestations: prestationsReception
      });
    }
    
    // Séparer les événements avec date et sans date
    const eventsWithDate = events.filter(e => e.date && e.date.getFullYear() < 2099);
    const eventsWithoutDate = events.filter(e => !e.date || e.date.getFullYear() >= 2099);
    
    // TRI PAR DATE PUIS HEURE pour les événements avec date
    eventsWithDate.sort((a, b) => {
      const dateDiff = a.date - b.date;
      if (dateDiff !== 0) return dateDiff;
      if (a.hour && b.hour) return a.hour.localeCompare(b.hour);
      return 0;
    });
    
    // Grouper par jour
    const dayGroups = {};
    eventsWithDate.forEach(evt => {
      const dateKey = evt.date.toISOString().split('T')[0];
      if (!dayGroups[dateKey]) {
        dayGroups[dateKey] = { date: evt.date, events: [] };
      }
      dayGroups[dateKey].events.push(evt);
    });
    
    // Ajouter les événements sans date à la fin
    if (eventsWithoutDate.length > 0) {
      const noDateKey = 'no-date';
      dayGroups[noDateKey] = { date: null, events: eventsWithoutDate };
    }
    
    const sortedKeys = Object.keys(dayGroups).filter(k => k !== 'no-date').sort((a, b) => new Date(a) - new Date(b));
    if (dayGroups['no-date']) {
      sortedKeys.push('no-date');
    }
    
    const days = sortedKeys.map((key, idx) => {
      const isNoDate = key === 'no-date';
      const isLastDay = !isNoDate && idx === sortedKeys.filter(k => k !== 'no-date').length - 1;
      return {
        dayNumber: isNoDate ? null : idx + 1,
        dayLabel: isNoDate ? 'À planifier' : (isLastDay ? 'Jour J' : `Jour ${idx + 1}`),
        date: dayGroups[key].date,
        events: dayGroups[key].events
      };
    });

    return (
      <div className="space-y-6">
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-900 mb-1">✨ Inclus dans votre prestation</p>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Film version longue + Teaser</li>
                  <li>• Intégrale du reportage photo en HD</li>
                  <li>• {getUSBInfo()}</li>
                  <li>• Vin d&apos;honneur (30min inclus)</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {days.map(day => (
          <Card key={day.dayNumber} className="border-2 border-amber-200">
            <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
              <CardTitle className="text-xl flex items-center gap-3">
                <span className="bg-amber-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold">
                  {day.dayLabel === 'Jour J' ? '💍' : (day.dayLabel === 'À planifier' ? '📅' : day.dayNumber)}
                </span>
                <div>
                  <div className="text-lg font-bold text-stone-900">{day.dayLabel}</div>
                  {day.date && (
                  <div className="text-sm font-normal text-stone-600">
                    {day.date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {day.events.map((evt, idx) => (
                <div key={idx}>
                  <div className="flex items-center gap-2 mb-3">
                    <h4 className="font-semibold text-stone-800 text-base">
                      {evt.icon} {evt.label}
                    </h4>
                    {evt.hour && (
                      <Badge className="bg-stone-200 text-stone-700 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {evt.hour}
                      </Badge>
                    )}
                  </div>
                  {evt.prestations.length > 0 && (
                    <div className="space-y-3 mt-3">
                      {evt.prestations.map(p => renderPrestationWithOptions(p))}
                    </div>
                  )}
                  {idx < day.events.length - 1 && <Separator className="my-4" />}
                </div>
              ))}
            </CardContent>
          </Card>
        ))}

        {(() => {
          const relevantMagnets = getRelevantMagnets();
          return relevantMagnets.length > 0 && (
            <Card>
              <CardHeader className="bg-purple-50">
                <CardTitle className="text-lg flex items-center gap-2">🧲 Formules Magnets</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-2">
                {relevantMagnets.map(m => {
                  const isSelected = selectedPrestations.includes(m.id);
                  return (
                    <div 
                      key={m.id} 
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer ${isSelected ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:bg-stone-50'}`}
                      onClick={() => handleToggle(m.id)}
                    >
                      <Checkbox checked={isSelected} onCheckedChange={() => handleToggle(m.id)} className="mt-1" />
                      <div className="flex-1">
                        <label 
                          className="font-medium text-stone-900 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggle(m.id);
                          }}
                        >
                          {m.name}
                        </label>
                      </div>
                      {showPrices && (
                        <div className="text-right">
                          <p className="font-bold text-stone-900">{m.price_ht.toFixed(2)}€</p>
                          <p className="text-xs text-stone-500">HT</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          );
        })()}

        {generalOptions.length > 0 && (
          <Card>
            <CardHeader className="bg-stone-50">
              <CardTitle className="text-lg flex items-center gap-2">⚙️ Options Générales</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-2">
              {generalOptions.map(opt => {
                const isSelected = selectedPrestations.includes(opt.id);
                return (
                  <div 
                    key={opt.id} 
                    className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer ${isSelected ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:bg-stone-50'}`}
                    onClick={() => handleToggle(opt.id)}
                  >
                    <Checkbox checked={isSelected} onCheckedChange={() => handleToggle(opt.id)} className="mt-1" />
                    <div className="flex-1">
                      <label 
                        className="font-medium text-stone-900 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggle(opt.id);
                        }}
                      >
                        {opt.name}
                      </label>
                    </div>
                    {showPrices && (
                      <div className="text-right">
                        <p className="font-bold text-stone-900">{opt.price_ht.toFixed(2)}€</p>
                        <p className="text-xs text-stone-500">HT</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {showPrices && selectedPrestations.length > 0 && (
          <Card className="border-amber-500 border-2">
            <CardContent className="p-4">
              <div className="space-y-2">
                <div className="flex justify-between text-stone-700">
                  <span>Total HT</span>
                  <span className="font-semibold">{totalHT.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between text-stone-700">
                  <span>TVA (20%)</span>
                  <span className="font-semibold">{(totalTTC - totalHT).toFixed(2)}€</span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-bold text-amber-600">
                  <span>Total TTC</span>
                  <span>{totalTTC.toFixed(2)}€</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // === BAR MITZVAH : UN SEUL BLOC MÊME SI 2 JOURS ===
  if (eventType === 'barmitzvah') {
    const mainPrests = prestations.filter(p => 
      p.category !== 'magnets' && 
      p.category !== 'option' &&
      p.prestation_type !== 'magnets'
    );

    return (
      <div className="space-y-6">
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-900 mb-1">✨ Inclus dans votre prestation</p>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Film version longue + Teaser</li>
                  <li>• Intégrale du reportage photo en HD</li>
                  <li>• {getUSBInfo()}</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
            <CardTitle className="text-lg">📸 Prestations Bar Mitzvah</CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {mainPrests.map(p => renderPrestationWithOptions(p))}
          </CardContent>
        </Card>

        {(() => {
          const relevantMagnets = getRelevantMagnets();
          return relevantMagnets.length > 0 && (
            <Card>
              <CardHeader className="bg-purple-50">
                <CardTitle className="text-lg flex items-center gap-2">🧲 Formules Magnets</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-2">
                {relevantMagnets.map(m => {
                  const isSelected = selectedPrestations.includes(m.id);
                  return (
                    <div 
                      key={m.id} 
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer ${isSelected ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:bg-stone-50'}`}
                      onClick={() => handleToggle(m.id)}
                    >
                      <Checkbox checked={isSelected} onCheckedChange={() => handleToggle(m.id)} className="mt-1" />
                      <div className="flex-1">
                        <label 
                          className="font-medium text-stone-900 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggle(m.id);
                          }}
                        >
                          {m.name}
                        </label>
                      </div>
                      {showPrices && (
                        <div className="text-right">
                          <p className="font-bold text-stone-900">{m.price_ht.toFixed(2)}€</p>
                          <p className="text-xs text-stone-500">HT</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          );
        })()}

        {generalOptions.length > 0 && (
          <Card>
            <CardHeader className="bg-stone-50">
              <CardTitle className="text-lg flex items-center gap-2">⚙️ Options Supplémentaires</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-2">
              {generalOptions.map(opt => {
                const isSelected = selectedPrestations.includes(opt.id);
                return (
                  <div 
                    key={opt.id} 
                    className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer ${isSelected ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:bg-stone-50'}`}
                    onClick={() => handleToggle(opt.id)}
                  >
                    <Checkbox checked={isSelected} onCheckedChange={() => handleToggle(opt.id)} className="mt-1" />
                    <div className="flex-1">
                      <label 
                        className="font-medium text-stone-900 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggle(opt.id);
                        }}
                      >
                        {opt.name}
                      </label>
                    </div>
                    {showPrices && (
                      <div className="text-right">
                        <p className="font-bold text-stone-900">{opt.price_ht.toFixed(2)}€</p>
                        <p className="text-xs text-stone-500">HT</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {showPrices && selectedPrestations.length > 0 && (
          <Card className="border-amber-500 border-2">
            <CardContent className="p-4">
              <div className="space-y-2">
                <div className="flex justify-between text-stone-700">
                  <span>Total HT</span>
                  <span className="font-semibold">{totalHT.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between text-stone-700">
                  <span>TVA (20%)</span>
                  <span className="font-semibold">{(totalTTC - totalHT).toFixed(2)}€</span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-bold text-amber-600">
                  <span>Total TTC</span>
                  <span>{totalTTC.toFixed(2)}€</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // === AUTRES (BRIT MILA, COUPE CHEVEUX) ===
  const mainPrests = prestations.filter(p => 
    p.category !== 'magnets' && 
    p.category !== 'option' &&
    p.prestation_type !== 'magnets'
  );

  return (
    <div className="space-y-6">
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-900 mb-1">✨ Inclus dans votre prestation</p>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Film version longue + Teaser</li>
                <li>• Intégrale du reportage photo en HD</li>
                <li>• {getUSBInfo()}</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {mainPrests.length > 0 && (
        <Card>
          <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
            <CardTitle className="text-lg">📸 Prestations</CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {mainPrests.map(p => renderPrestationWithOptions(p))}
          </CardContent>
        </Card>
      )}

      {(() => {
        const relevantMagnets = getRelevantMagnets();
        return relevantMagnets.length > 0 && (
          <Card>
            <CardHeader className="bg-purple-50">
              <CardTitle className="text-lg flex items-center gap-2">🧲 Formules Magnets</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-2">
              {relevantMagnets.map(m => {
                const isSelected = selectedPrestations.includes(m.id);
                return (
                  <div key={m.id} className={`flex items-start gap-3 p-3 border rounded-lg ${isSelected ? 'border-amber-500 bg-amber-50' : 'border-stone-200'}`}>
                    <Checkbox checked={isSelected} onCheckedChange={() => handleToggle(m.id)} className="mt-1" />
                    <div className="flex-1">
                      <label className="font-medium text-stone-900 cursor-pointer">{m.name}</label>
                    </div>
                    {showPrices && (
                      <div className="text-right">
                        <p className="font-bold text-stone-900">{m.price_ht.toFixed(2)}€</p>
                        <p className="text-xs text-stone-500">HT</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })()}

      {generalOptions.length > 0 && (
        <Card>
          <CardHeader className="bg-stone-50">
            <CardTitle className="text-lg flex items-center gap-2">⚙️ Options Supplémentaires</CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-2">
            {generalOptions.map(opt => {
              const isSelected = selectedPrestations.includes(opt.id);
              return (
                <div key={opt.id} className={`flex items-start gap-3 p-3 border rounded-lg ${isSelected ? 'border-amber-500 bg-amber-50' : 'border-stone-200'}`}>
                  <Checkbox checked={isSelected} onCheckedChange={() => handleToggle(opt.id)} className="mt-1" />
                  <div className="flex-1">
                    <label className="font-medium text-stone-900 cursor-pointer">{opt.name}</label>
                  </div>
                  {showPrices && (
                    <div className="text-right">
                      <p className="font-bold text-stone-900">{opt.price_ht.toFixed(2)}€</p>
                      <p className="text-xs text-stone-500">HT</p>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {showPrices && selectedPrestations.length > 0 && (
        <Card className="border-amber-500 border-2">
          <CardContent className="p-4">
            <div className="space-y-2">
              <div className="flex justify-between text-stone-700">
                <span>Total HT</span>
                <span className="font-semibold">{totalHT.toFixed(2)}€</span>
              </div>
              <div className="flex justify-between text-stone-700">
                <span>TVA (20%)</span>
                <span className="font-semibold">{(totalTTC - totalHT).toFixed(2)}€</span>
              </div>
              <Separator />
              <div className="flex justify-between text-lg font-bold text-amber-600">
                <span>Total TTC</span>
                <span>{totalTTC.toFixed(2)}€</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default QuestionnaireRecap;
